import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Drawer, Dropdown, Flex, Grid, Input, Layout, Menu } from 'antd';
import dayjs from 'dayjs';
import { ReactNode, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { DeliveryIcon } from '../store/icon';

const { Header, Content } = Layout;

/** True on phones and small tablets (below antd's `lg` breakpoint, 992px). */
export const useIsMobile = () => {
  const screens = Grid.useBreakpoint();
  // Before the first measurement `screens` is empty; fall back to window width.
  if (screens.lg === undefined) {
    return typeof window !== 'undefined' && window.innerWidth < 992;
  }
  return !screens.lg;
};

interface AppShellProps {
  user: any;
  items: any[];
  keys: string[];
  openKeys: string[];
  onSearch: (value: string) => void;
  onLogout: () => void;
  logoPadding?: string;
  children?: ReactNode;
}

/**
 * Shared admin/customer layout.
 * Desktop: the original fixed sidebar with collapse button.
 * Mobile: a compact sticky header with a hamburger that opens the menu in a drawer.
 */
const AppShell = ({
  user,
  items,
  keys,
  openKeys,
  onSearch,
  onLogout,
  logoPadding = '17px 17px 17px 25px',
}: AppShellProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();

  // Close the mobile menu after navigating.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  const brand = (
    <div className="logo" style={{ textAlign: 'center', padding: logoPadding }}>
      <Link to="/">
        <DeliveryIcon />
        <span className="title"> My Delivery</span>
      </Link>
    </div>
  );

  const userInfo = (
    <div style={{ paddingLeft: 25, paddingBottom: 10 }}>
      <div>{user?.username}</div>
      <div>{user?.role?.roleName}</div>
    </div>
  );

  const userMenu = (
    <Dropdown
      menu={{
        items: [
          {
            key: 'profile',
            icon: <UserOutlined />,
            label: 'Профайл',
          },
          { type: 'divider' },
          {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Гарах',
          },
        ],
        onClick: ({ key }) => {
          if (key === 'profile') {
            navigate('/profile');
          } else if (key === 'logout') {
            onLogout();
          }
        },
      }}
      trigger={isMobile ? ['click'] : ['hover']}
    >
      <Avatar
        size={isMobile ? 'default' : 'small'}
        style={
          isMobile
            ? { backgroundColor: '#87d068', cursor: 'pointer', flexShrink: 0 }
            : { backgroundColor: '#87d068' }
        }
        icon={<UserOutlined />}
      />
    </Dropdown>
  );

  return (
    <Layout className={isMobile ? 'app-shell is-mobile' : 'app-shell'}>
      {!isMobile && (
        <Layout.Sider
          trigger={null}
          collapsible
          width={250}
          collapsed={collapsed}
          collapsedWidth={80}
          className="admin-sidebar"
        >
          <div>
            {brand}
            {userInfo}
            <Menu
              theme="light"
              mode="inline"
              defaultSelectedKeys={keys}
              defaultOpenKeys={openKeys}
              items={items}
            />
          </div>
        </Layout.Sider>
      )}
      {isMobile && (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={280}
          closable={false}
          rootClassName="mobile-nav-drawer"
          styles={{ body: { padding: 0 } }}
        >
          {brand}
          {userInfo}
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={keys}
            defaultOpenKeys={openKeys}
            items={items}
          />
        </Drawer>
      )}
      <Layout className="site-layout">
        <Header className="admin-header">
          <Flex align="center" justify="space-between" gap={isMobile ? 8 : 0}>
            {isMobile ? (
              <Flex align="center" gap={4} style={{ flexShrink: 0 }}>
                <Button
                  type="text"
                  aria-label="Цэс"
                  className="mobile-menu-btn"
                  icon={<MenuOutlined />}
                  onClick={() => setDrawerOpen(true)}
                  style={{ fontSize: 18, width: 48, height: 48 }}
                />
                <Link to="/dashboard" className="mobile-brand" aria-label="My Delivery">
                  <DeliveryIcon />
                </Link>
              </Flex>
            ) : (
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{
                  fontSize: '16px',
                  width: 64,
                  height: 64,
                }}
              />
            )}
            <Flex
              align="center"
              gap={isMobile ? 10 : 16}
              style={isMobile ? { marginRight: 12, flex: 1, minWidth: 0 } : { marginRight: 16 }}
            >
              <Input.Search
                size={isMobile ? 'middle' : 'small'}
                allowClear
                onSearch={onSearch}
                placeholder={isMobile ? 'Хайх' : undefined}
                style={isMobile ? { flex: 1, minWidth: 0 } : undefined}
              />
              {userMenu}
            </Flex>
          </Flex>
        </Header>
        <Content className="admin-container">
          <Outlet />
        </Content>
        <Layout.Footer className="admin-footer" style={{ textAlign: 'center' }}>
          MyDelivery System ©{dayjs().format('YYYY')} Created by MongolPost
        </Layout.Footer>
      </Layout>
    </Layout>
  );
};

export default AppShell;
